import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from '../utils/axiosInstance';
import '../assets/css/adminpanel.css';
import '../assets/css/player.css';

const PlayerManagement = () => {
    const [players, setPlayers] = useState([]);
    const [form, setForm] = useState({ name: '', color: '#ff0000' });
    const [editingId, setEditingId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [sortField, setSortField] = useState('');
    const [sortOrder, setSortOrder] = useState('asc');

    useEffect(() => {
        fetchPlayers();
    }, []);

    const fetchPlayers = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await axios.get('/players');
            setPlayers(response.data);
        } catch (err) {
            console.error('Error fetching players:', err);
            setError('Failed to fetch players. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        try {
            if (editingId) {
                await axios.put(`/players/${editingId}`, form);
            } else {
                await axios.post('/players', form);
            }
            setForm({ name: '', color: '#ff0000' });
            setEditingId(null);
            fetchPlayers();
        } catch (err) {
            console.error('Error saving player:', err);
            setError('Failed to save player. Please try again.');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this player?')) return;

        setError('');
        try {
            await axios.delete(`/players/${id}`);
            fetchPlayers();
        } catch (err) {
            console.error('Error deleting player:', err);
            setError('Failed to delete player. Please try again.');
        }
    };

    const handleEdit = (player) => {
        setForm({ name: player.name, color: player.color });
        setEditingId(player._id);
    };

    const handleSort = (field) => {
        const order = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
        setSortField(field);
        setSortOrder(order);

        const sortedPlayers = [...players].sort((a, b) => {
            if (a[field] < b[field]) return order === 'asc' ? -1 : 1;
            if (a[field] > b[field]) return order === 'asc' ? 1 : -1;
            return 0;
        });
        setPlayers(sortedPlayers);
    };

    return (
        <div className="admin-panel container player-management">
            <header>
                <h2>Player Management</h2>
                <p>Manage your players below. Add, edit, or delete players as needed.</p>
                <nav>
                    <Link to="/admin" className="admin-link">Back to Admin Panel</Link>
                </nav>
            </header>
            {loading && <p className="loading">Loading players...</p>}
            {error && <p className="error">{error}</p>}
            <div className="content">
                <section className="form-section">
                    <h2>{editingId ? 'Edit Player' : 'Add Player'}</h2>
                    <form onSubmit={handleSubmit}>
                        <div className="input-row">
                            <input
                                id="player-color"
                                type="color"
                                value={form.color}
                                onChange={(e) => setForm({ ...form, color: e.target.value })}
                            />
                            <input
                                id="player-name"
                                type="text"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                required
                                placeholder="Enter player name"
                            />
                        </div>
                        <button type="submit" className="button button-primary">
                            {editingId ? 'Update Player' : 'Add Player'}
                        </button>
                    </form>
                </section>
                <section className="table-section">
                    <table>
                        <thead>
                            <tr>
                                <th onClick={() => handleSort('name')}>
                                    Name {sortField === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
                                </th>
                                <th onClick={() => handleSort('color')}>
                                    Color {sortField === 'color' && (sortOrder === 'asc' ? '↑' : '↓')}
                                </th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {players.length > 0 ? (
                                players.map((player) => (
                                    <tr key={player._id}>
                                        <td>{player.name}</td>
                                        <td>
                                            <div
                                                className="color-box"
                                                style={{ backgroundColor: player.color }}
                                            />
                                        </td>
                                        <td>
                                            <button
                                                className="button small"
                                                onClick={() => handleEdit(player)}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                className="button secondary small"
                                                onClick={() => handleDelete(player._id)}
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="3">No players found. Add a new player!</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </section>
            </div>
        </div>
    );
};

export default PlayerManagement;